import React, { useState, useEffect } from 'react'
import { useAuth } from '@/context/AuthContext'
import { useRestaurant } from '@/context/RestaurantContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DateRangeModal } from '@/components/ui/DateRangeModal'
import { MobileFloatingFilter } from '@/components/ui/MobileFloatingFilter'
import {
  TrendingUp,
  DollarSign,
  ShoppingCart,
  CreditCard,
  Clock,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Calendar,
  PieChart,
  BarChart3,
  Search
} from 'lucide-react'
import { useCurrency } from '@/hooks/useCurrency'
import { ApiService } from '@/services/api'
import { formatDateForInput, createDateRange, parseDate } from '@/lib/dateUtils'
import { useBusinessDayDefaultRange } from '@/hooks/useBusinessDayDefaultRange'
import type { AnalyticsData, DateRange } from '@/types'

export default function AnalyticsScreen() {
  const { formatCurrency, currencySymbol } = useCurrency()
  const { user } = useAuth()
  const { currentRestaurant } = useRestaurant()
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dateRange, setDateRange] = useBusinessDayDefaultRange()
  const [pendingDateRange, setPendingDateRange] = useBusinessDayDefaultRange()
  const [topItemsSearch, setTopItemsSearch] = useState('')
  const [visibleItemsCount, setVisibleItemsCount] = useState(10)

  const loadAnalytics = async () => {
    if (!currentRestaurant?._id) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await ApiService.getAnalytics(currentRestaurant._id, dateRange)
      if (response.success) {
        setAnalytics(response.data)
        setVisibleItemsCount(10)
      } else {
        setError(response.message || 'Failed to load analytics')
      }
    } catch (err: unknown) {
      const errorMessage = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message || 'Failed to load analytics'
        : 'Failed to load analytics'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (currentRestaurant?._id) {
      loadAnalytics()
    }
  }, [currentRestaurant?._id, dateRange])

  const handleDateChange = (field: 'from' | 'to', value: string) => {
    const newDate = parseDate(value)
    setPendingDateRange(prev =>
      field === 'from'
        ? createDateRange(newDate, prev.to)
        : createDateRange(prev.from, newDate)
    )
  }

  const handleApply = () => {
    setDateRange(pendingDateRange)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading analytics...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button 
              variant="outline" 
              size="sm" 
              className="ml-2"
              onClick={loadAnalytics}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!analytics) {
    return (
      <div className="space-y-6">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            No analytics data available for the selected period.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold truncate">Analytics</h1>
          <p className="text-muted-foreground text-sm sm:text-base truncate">
            Performance metrics
          </p>
        </div>
        <Button onClick={loadAnalytics} variant="outline" size="icon" className="py-[10px] sm:w-auto sm:h-auto sm:px-4 flex-shrink-0">
          <RefreshCw className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Date Range Selector - Desktop Only */}
      <div className="hidden md:flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex-1">
          <DateRangeModal
            dateRange={pendingDateRange}
            onDateChange={handleDateChange}
            onApply={handleApply}
            title="Analytics Date Range"
            description="Select the date range for your analytics report"
          />
        </div>
        <Button
          onClick={handleApply}
          className="sm:w-auto flex items-center justify-center gap-2"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4" />
              Refresh
            </>
          )}
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(analytics.totalRevenue)}</div>
            <p className="text-xs text-muted-foreground">
              For selected period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Bills</CardTitle>
            <Clock className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-orange-500">{formatCurrency(analytics.activeBillsTotal ?? 0)}</div>
            <p className="text-xs text-muted-foreground">
              Unsettled for selected period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{analytics.totalOrders}</div>
            <p className="text-xs text-muted-foreground">
              Orders completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Order Value</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(analytics.averageOrderValue)}</div>
            <p className="text-xs text-muted-foreground">
              Per order average
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Peak Hour</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">
              {analytics.hourlyRevenue?.reduce((peak, current) =>
                current.revenue > peak.revenue ? current : peak
              )?.hour || 0}:00
            </div>
            <p className="text-xs text-muted-foreground">
              Highest revenue hour
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Payment Methods Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CreditCard className="h-5 w-5" />
            <span>Payment Methods</span>
          </CardTitle>
          <CardDescription>
            Revenue breakdown by payment method
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(() => {
            const methods = analytics.paymentMethodBreakdown
              ? Object.entries(analytics.paymentMethodBreakdown).sort(([, a], [, b]) => b.revenue - a.revenue)
              : []
            return (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Method</TableHead>
                        <TableHead className="text-right">Orders</TableHead>
                        <TableHead className="text-right">% of Orders</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {methods.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                            No payment data available.
                          </TableCell>
                        </TableRow>
                      ) : (
                        methods.map(([method, data]) => (
                          <TableRow key={method}>
                            <TableCell className="font-medium capitalize">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-blue-500 flex-shrink-0"></div>
                                {method}
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">{data.count}</TableCell>
                            <TableCell className="text-right">
                              <Badge variant="secondary" className="text-xs">{data.percentage.toFixed(1)}%</Badge>
                            </TableCell>
                            <TableCell className="text-right font-semibold">{formatCurrency(data.revenue)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-2">
                  {methods.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4 text-sm">No payment data available.</p>
                  ) : (
                    methods.map(([method, data]) => (
                      <div key={method} className="flex items-center justify-between gap-3 rounded-lg border p-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-3 h-3 rounded-full bg-blue-500 flex-shrink-0"></div>
                          <span className="font-medium capitalize text-sm truncate">{method}</span>
                          <Badge variant="secondary" className="text-xs flex-shrink-0">{data.percentage.toFixed(1)}%</Badge>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="font-semibold text-sm">{formatCurrency(data.revenue)}</div>
                          <div className="text-xs text-muted-foreground">{data.count} orders</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )
          })()}
        </CardContent>
      </Card>

      {/* Top Selling Items */}
      {(() => {
        const filteredItems = analytics.topSellingItems
          ?.filter(item => item.name.toLowerCase().includes(topItemsSearch.toLowerCase())) || []
        const displayedItems = topItemsSearch ? filteredItems : filteredItems.slice(0, visibleItemsCount)
        const hasMore = !topItemsSearch && filteredItems.length > visibleItemsCount

        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5" />
                <span>Top Selling Items</span>
              </CardTitle>
              <CardDescription>
                {filteredItems.length} menu item{filteredItems.length !== 1 ? 's' : ''} ranked by item price (excl. tax/charges)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search items..."
                    value={topItemsSearch}
                    onChange={(e) => {
                      setTopItemsSearch(e.target.value)
                      setVisibleItemsCount(10)
                    }}
                    className="pl-10"
                  />
                </div>
              </div>
              {/* Desktop Table */}
              <div className="hidden lg:block">
                <div className="max-h-[600px] overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background">
                      <TableRow>
                        <TableHead className="w-14">Rank</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Units Sold</TableHead>
                        <TableHead className="text-right">Item Price</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {displayedItems.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                            No items match your search.
                          </TableCell>
                        </TableRow>
                      ) : (
                        displayedItems.map((item, index) => (
                          <TableRow key={item.name}>
                            <TableCell>
                              <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                                {index + 1}
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">{item.name}</TableCell>
                            <TableCell className="text-right text-muted-foreground">{item.quantity}</TableCell>
                            <TableCell className="text-right">
                              <div className="font-semibold">{formatCurrency(item.revenue)}</div>
                              {item.chargesAmount > 0.5 && (
                                <div className="text-xs font-normal text-muted-foreground">
                                  +{formatCurrency(item.chargesAmount)} tax/charges = {formatCurrency(item.totalRevenue)}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-2">
                {displayedItems.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4 text-sm">No items match your search.</p>
                ) : (
                  displayedItems.map((item, index) => (
                    <div key={item.name} className="flex items-center justify-between gap-3 rounded-lg border p-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {index + 1}
                        </div>
                        <span className="font-medium text-sm truncate">{item.name}</span>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-semibold text-sm">{formatCurrency(item.revenue)}</div>
                        <div className="text-xs text-muted-foreground">{item.quantity} sold</div>
                        {item.chargesAmount > 0.5 && (
                          <div className="text-xs text-muted-foreground">
                            +{formatCurrency(item.chargesAmount)} tax = {formatCurrency(item.totalRevenue)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {hasMore && (
                <Button
                  variant="outline"
                  className="w-full mt-3 sm:mt-4"
                  onClick={() => setVisibleItemsCount(prev => prev + 10)}
                >
                  Load More ({filteredItems.length - visibleItemsCount} remaining)
                </Button>
              )}
            </CardContent>
          </Card>
        )
      })()}

      {/* Hourly Revenue Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <PieChart className="h-5 w-5" />
            <span>Hourly Revenue Distribution</span>
          </CardTitle>
          <CardDescription>
            Revenue pattern throughout the day
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 sm:space-y-3">
            {analytics.hourlyRevenue?.map((hour) => (
              <div key={hour.hour} className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-8 sm:w-12 text-xs sm:text-sm text-muted-foreground text-right">
                  {hour.hour}:00
                </div>
                <div className="flex-1 relative">
                  <div className="h-4 sm:h-6 bg-muted rounded">
                    <div 
                      className="h-full bg-gradient-to-r from-green-400 to-blue-500 rounded"
                      style={{ 
                        width: `${(hour.revenue / Math.max(...analytics.hourlyRevenue.map(h => h.revenue))) * 100}%` 
                      }}
                    ></div>
                  </div>
                </div>
                <div className="w-16 sm:w-24 text-xs sm:text-sm text-right">
                  {formatCurrency(hour.revenue)}
                </div>
                <div className="w-12 sm:w-16 text-xs sm:text-sm text-muted-foreground text-right">
                  {hour.orders}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Mobile Floating Filter */}
      <MobileFloatingFilter
        dateRange={pendingDateRange}
        onDateChange={handleDateChange}
        onApply={handleApply}
        title="Analytics Date Range"
        description="Select the date range for your analytics report"
      />
    </div>
  )
}